/**
 * Perfil do usuário autenticado — GET/PATCH /api/v1/me (DOC 06 · LAC-070/067).
 */
import axios from "axios";
import config from "../config";
import { APIClient } from "./api_helper";
import { GET_ME } from "./url_helper";

const api = new APIClient();
const apiBase = config.api.API_URL;

export type MeUser = {
  id: number;
  name: string;
  email: string;
  locale: string | null;
  /** Fuso pessoal de exibição; null = herda a conta. */
  timezone?: string | null;
  avatar_url?: string | null;
  roles?: string[];
  mfa_enabled?: boolean;
  /** Conta Google vinculada (LAC-045). */
  google_linked?: boolean;
  /** false = login só OAuth; MFA/senha local exigem senha definida. */
  has_password?: boolean;
};

export type MeTenant = {
  id: number;
  name: string;
  locale: string;
  timezone: string;
  logo_url?: string | null;
  has_logo?: boolean;
  tracking_hostname?: string | null;
  /** Ramp F1 — teto diário no disparo (DOC 14 § 10.3). */
  sending_ramp?: {
    stage: "new" | "warming" | "trusted" | string;
    sent_today: number;
    daily_cap: number | null;
    remaining_today: number | null;
  };
  /** Ritmo 1 e-mail / N s — piso SA + preferência efetiva. */
  sending_pace?: {
    interval_seconds: number;
    min_interval_seconds: number;
    max_interval_seconds: number;
  };
};

export type MePayload = {
  user: MeUser;
  tenant: MeTenant;
  impersonation?: {
    active: boolean;
    impersonator_id: number;
    expires_at: string | null;
  } | null;
};

export type UpdateProfilePayload = {
  name?: string;
  email?: string;
  password?: string;
  password_confirmation?: string;
  locale?: string;
  /** Relógio da conta (tenants.timezone) — agendamento. */
  timezone?: string;
  /** Fuso pessoal (users.timezone); null limpa. */
  user_timezone?: string | null;
};

/** Fusos BR + comuns — labels em `timezone.label.*` (DOC 12). */
export const ACCOUNT_TIMEZONE_OPTIONS = [
  "America/Sao_Paulo",
  "America/Fortaleza",
  "America/Recife",
  "America/Bahia",
  "America/Belem",
  "America/Manaus",
  "America/Cuiaba",
  "America/Porto_Velho",
  "America/Rio_Branco",
  "America/Noronha",
  "America/Argentina/Buenos_Aires",
  "America/Santiago",
  "America/Mexico_City",
  "America/New_York",
  "Europe/Lisbon",
  "Europe/Madrid",
  "UTC",
] as const;

export async function fetchMe(): Promise<MePayload> {
  const envelope: any = await api.get(GET_ME);
  if (!envelope?.success || !envelope?.data?.user) {
    throw new Error(envelope?.error?.message || "me_failed");
  }
  return envelope.data as MePayload;
}

export async function updateProfile(
  payload: UpdateProfilePayload
): Promise<{ data: MePayload; message?: string }> {
  const envelope: any = await api.update(GET_ME, payload);
  if (!envelope?.success || !envelope?.data?.user) {
    const msg =
      envelope?.error?.message ||
      (typeof envelope?.message === "string" ? envelope.message : null);
    throw new Error(msg || "profile_update_failed");
  }
  return { data: envelope.data as MePayload, message: envelope.message };
}

/** Upload do logo da marca (página de descadastro) — multipart. */
export async function uploadBrandLogo(
  file: File
): Promise<{ logo_url: string | null; has_logo: boolean; message?: string }> {
  const form = new FormData();
  form.append("logo", file);
  const envelope: any = await axios.post(apiBase + "/me/brand-logo", form, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData && headers) {
          delete (headers as Record<string, unknown>)["Content-Type"];
        }
        return data;
      },
    ],
  });
  if (!envelope?.success) {
    throw new Error(envelope?.error?.message || "logo_upload_failed");
  }
  return {
    logo_url: envelope.data?.logo_url ?? null,
    has_logo: Boolean(envelope.data?.has_logo),
    message: envelope.message,
  };
}

export async function deleteBrandLogo(): Promise<{
  logo_url: null;
  has_logo: false;
  message?: string;
}> {
  const envelope: any = await api.delete(GET_ME + "/brand-logo");
  if (!envelope?.success) {
    throw new Error(envelope?.error?.message || "logo_delete_failed");
  }
  return {
    logo_url: null,
    has_logo: false,
    message: envelope.message,
  };
}

/** Upload da foto de perfil (header) — multipart (LAC-072). */
export async function uploadUserAvatar(
  file: File
): Promise<{ avatar_url: string | null; has_avatar: boolean; message?: string }> {
  const form = new FormData();
  form.append("avatar", file);
  const envelope: any = await axios.post(apiBase + "/me/avatar", form, {
    transformRequest: [
      (data, headers) => {
        if (data instanceof FormData && headers) {
          delete (headers as Record<string, unknown>)["Content-Type"];
        }
        return data;
      },
    ],
  });
  if (!envelope?.success) {
    throw new Error(envelope?.error?.message || "avatar_upload_failed");
  }
  return {
    avatar_url: envelope.data?.avatar_url ?? null,
    has_avatar: Boolean(envelope.data?.has_avatar),
    message: envelope.message,
  };
}

export async function deleteUserAvatar(): Promise<{
  avatar_url: null;
  has_avatar: false;
  message?: string;
}> {
  const envelope: any = await api.delete(GET_ME + "/avatar");
  if (!envelope?.success) {
    throw new Error(envelope?.error?.message || "avatar_delete_failed");
  }
  return {
    avatar_url: null,
    has_avatar: false,
    message: envelope.message,
  };
}
